import { Avatar, Box, Skeleton, Stack } from '@mui/material';
import React, { ReactNode } from 'react';
import EditablePanel, { EditablePanelProps } from '../../../../components/EditablePanel';
import EllipsisText from '../../../../components/EllipsisText';
import { useI18n } from '../../../../i18n/I18n';
import { getInitials } from '../../../../nav/UserAvatar';
import { APPROVAL_EDITABLE_PANEL_CONTENT_SX, APPROVAL_STRATEGIES } from '../../../constants';
import { useReviewApprovalContext } from '../../hooks/useReviewApprovalContext';
import CreateApprovalFormEditablePanelHeader from '../CreateApprovalFormEditablePanelHeader';
import ApprovalStatusIndicator from './ApprovalStatusIndicator';

const headerActionsSx = { flex: 1 };

export type ApprovalApproversViewProps = Pick<EditablePanelProps, 'isCollapsable' | 'defaultCollapsed'>;

export default function ApprovalApproversView({
    isCollapsable = true,
    defaultCollapsed = false
}: ApprovalApproversViewProps) {
    const { isFetchingApprovalData, approvalData } = useReviewApprovalContext();

    const unknownName = useI18n('approval.approver.unknownName');
    const allApprovalsRequiredLabel = useI18n('approval.strategy.allApprovalsRequired');
    const anyOneApprovalRequiredLabel = useI18n('approval.strategy.anyOneApprovalRequired');
    const allApprovalsRequiredInSequenceLabel = useI18n('approval.strategy.allApprovalsRequiredInSequence');
    const approvalStrategyLabelMap = {
        [APPROVAL_STRATEGIES.allApprovalsRequired]: allApprovalsRequiredLabel,
        [APPROVAL_STRATEGIES.anyOneApprovalRequired]: anyOneApprovalRequiredLabel,
        [APPROVAL_STRATEGIES.allApprovalsRequiredInSequence]: allApprovalsRequiredInSequenceLabel
    };

    let approversContent: ReactNode = null;

    if (isFetchingApprovalData) {
        approversContent = Array.from({ length: 3 }).map((_, skeletonIndex) => (
            <Skeleton key={skeletonIndex} variant="rounded" width="100%" height={42} />
        ));
    } else if (approvalData?.steps && approvalData.steps.length > 0) {
        approversContent = approvalData.steps.map((step) => {
            const contact = step.contact;
            const name = contact.name ? `${contact.name.firstName} ${contact.name.lastName}` : unknownName;
            const email = contact.contactInfo?.email?.address || '';
            const status = step.status;

            return (
                <Box key={step.id} display="flex" alignItems="center" gap={1}>
                    <Box flex={1} display="flex" gap={1} alignItems="center">
                        <Avatar
                            sx={(theme) => ({
                                color: 'text.primary',
                                backgroundColor:
                                    theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[700]
                            })}
                        >
                            {getInitials(contact.name ?? {})}
                        </Avatar>
                        <Box flexDirection="column" overflow="hidden">
                            <EllipsisText text={name} />
                            <EllipsisText text={email} sx={{ color: 'text.disabled', fontSize: 14 }} />
                        </Box>
                    </Box>
                    <ApprovalStatusIndicator status={status} />
                </Box>
            );
        });
    }

    return (
        <EditablePanel
            Header={
                <CreateApprovalFormEditablePanelHeader
                    titleToken="approval.approvers.header"
                    titleSuffix={
                        approvalData?.approvalStrategy ? approvalStrategyLabelMap[approvalData.approvalStrategy] : ''
                    }
                />
            }
            isCollapsable={isCollapsable}
            defaultCollapsed={defaultCollapsed}
            headerActionsSx={headerActionsSx}
        >
            <Stack sx={[APPROVAL_EDITABLE_PANEL_CONTENT_SX, { gap: 1.5 }]}>{approversContent}</Stack>
        </EditablePanel>
    );
}
